import { View, Text, ScreenContainer, Switch } from "@src/components";
import { Redirect, useRouter } from "expo-router";
import { useUnistyles, StyleSheet } from "react-native-unistyles";
import { useCallback, useEffect, useRef, useState } from "react";
import { FocusedSystemBars } from "@src/components/FocusedSystemBars";
import { BackgroundGradient } from "@src/components/BackgroundGradient";
import { FEATURE_FLAGS, useFeatureFlags } from "@src/services/feature-flags/feature-flags";
import { PermissionDeniedError, RegistrationCancelledError, useDeviceToken } from "@visioni/a3s-notifications/app";
import { getDeviceTokenApi } from "@src/features/notifications";
import { Icon } from "@src/components/Icon";
import { Button } from "@src/components/Button";
import { Linking, Pressable } from "react-native";
import { appStorage, AppStorageKeys } from "@src/services/app-storage";
import { useAppState } from "@src/hooks/useAppState";
import { showErrorToast } from "@src/utils/toast";

export default function PushNotificationsOnboardingScreen() {
  const router = useRouter();
  const { theme } = useUnistyles();

  const appStateStatus = useAppState();
  const deviceTokenApi = getDeviceTokenApi();

  const { getBooleanFlag } = useFeatureFlags();
  const pushFeatureEnabled = getBooleanFlag(FEATURE_FLAGS.PUSH_NOTIFICATIONS);

  const [switchEnabled, setSwitchEnabled] = useState(false);
  const shouldRemainRegisteredRef = useRef(false);
  const shouldRemainRegistered = useCallback((_token: string) => shouldRemainRegisteredRef.current, []);

  const { isRegistered, register, unregister } = useDeviceToken({
    deviceTokenApi,
    enabled: pushFeatureEnabled,
    shouldRemainRegistered,
  });
  const refetchIsRegistered = isRegistered.refetch;

  const isDeniedAtOsLevel = isRegistered.data?.permission === "denied";
  const isBusy = register.isPending || unregister.isPending;

  const handleToggle = () => {
    if (isDeniedAtOsLevel) {
      setSwitchEnabled(false);
      return;
    }

    const toggling = !switchEnabled;
    shouldRemainRegisteredRef.current = toggling;
    setSwitchEnabled(toggling);

    if (toggling) {
      register.mutate(undefined, {
        onSuccess: ({ token }) => {
          appStorage.set(AppStorageKeys.PUSH_NOTIFICATIONS_DEVICE_TOKEN, token);
        },
        onError: (error) => {
          setSwitchEnabled(false);
          if (!(error instanceof PermissionDeniedError) && !(error instanceof RegistrationCancelledError)) {
            showErrorToast({ message: "Something went wrong. Please try again." });
          }
        },
      });
    } else {
      const knownToken =
        appStorage.get<string>(AppStorageKeys.PUSH_NOTIFICATIONS_DEVICE_TOKEN) ?? isRegistered.data?.token;
      appStorage.remove(AppStorageKeys.PUSH_NOTIFICATIONS_DEVICE_TOKEN);
      unregister.mutate(
        { token: knownToken ?? undefined, lookupToken: false },
        {
          onError: () => {
            if (knownToken) {
              appStorage.set(AppStorageKeys.PUSH_NOTIFICATIONS_DEVICE_TOKEN, knownToken);
            }
            setSwitchEnabled(true);
            showErrorToast({ message: "Something went wrong. Please try again." });
          },
        },
      );
    }
  };

  const handleContinue = () => {
    appStorage.set(AppStorageKeys.PUSH_NOTIFICATIONS_ONBOARDING_SEEN, true);
    router.replace("/overview");
  };

  useEffect(() => {
    if (appStateStatus === "active") {
      refetchIsRegistered();
    }
  }, [appStateStatus, refetchIsRegistered]);

  if (!pushFeatureEnabled) {
    return <Redirect href="/overview" />;
  }

  return (
    <>
      <FocusedSystemBars style="light" />
      <BackgroundGradient>
        <ScreenContainer edges={["top", "bottom"]} style={{ backgroundColor: "transparent" }}>
          <View style={{ height: 56 }} />

          <View alignItems="center" justifyContent="center" mt="11xl">
            <Icon
              name={switchEnabled && !isDeniedAtOsLevel ? "bellOutline" : "bellOffOutline"}
              size={64}
              color={theme.colors.white.default}
            />
          </View>

          <View flex={1} px="4xl">
            <View mt="9xl" alignItems="center">
              <Text textStyle="h3/regular" textAlign="center" style={styles.text}>
                Push Notifications
              </Text>
            </View>

            <View mt="4xl" alignItems="center" gap="xl">
              <Text textStyle="sm/regular" textAlign="center" style={styles.text}>
                Get notified about alarm activations and other important activity on your sites.
              </Text>
            </View>

            <View mt="11xl">
              <View style={styles.card}>
                <View style={styles.row}>
                  <View flex={1}>
                    <Text textStyle="md/medium" style={styles.text}>
                      Enable Notifications
                    </Text>
                  </View>
                  <Switch
                    active={switchEnabled}
                    onToggle={handleToggle}
                    disabled={isBusy || isDeniedAtOsLevel}
                    size="medium"
                  />
                </View>
              </View>
            </View>

            {isDeniedAtOsLevel && (
              <Pressable style={styles.card} onPress={() => void Linking.openSettings()}>
                <View style={styles.row}>
                  <View flex={1}>
                    <Text textStyle="sm/medium" style={styles.text}>
                      Open System Settings
                    </Text>
                    <Text textStyle="xs/regular" style={styles.secondaryText} mt="xs">
                      Enable push notifications in system settings. Tap to open.
                    </Text>
                  </View>
                  <Icon name="chevronRight" size={theme.spacing["7xl"]} color={theme.colors.white.default} />
                </View>
              </Pressable>
            )}

            <View>
              <Button
                label={isDeniedAtOsLevel ? "Skip" : "Continue"}
                textStyle="md/regular"
                size="md"
                variant="primary"
                variantColor="blue"
                onPress={handleContinue}
                disabled={isBusy}
              />
            </View>
          </View>
        </ScreenContainer>
      </BackgroundGradient>
    </>
  );
}

const styles = StyleSheet.create((theme) => ({
  text: {
    color: theme.colors.interactive.buttons.default_foreground,
  },
  card: {
    borderRadius: theme.spacing.xl,
    borderWidth: 1,
    borderColor: theme.colors.interactive.controls.secondary_foreground,
    overflow: "hidden",
    marginBottom: theme.spacing["3xl"],
  },
  row: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: theme.spacing["3xl"],
    paddingHorizontal: theme.spacing.xl,
  },
  secondaryText: {
    color: theme.colors.interactive.controls.secondary_foreground,
  },
}));
